/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * @file DriverUI.js
 * @description
 * Premium building blocks of the Driver / Courier (Sofőr) app, written in the
 * "Happy Premium" language (orange brand, soft tinted chips, pill buttons, big
 * confident numbers) and tuned for on-the-road use: oversized touch targets
 * (>= 52 pt), high-contrast tone colours and a solid (never glassy) sheet shell.
 * Every primary action is an orange fill with white text.
 *
 * @module components/driver/DriverUI
 */

import React, {useCallback, useEffect, useRef} from 'react';
import {
    ActivityIndicator,
    Animated,
    KeyboardAvoidingView,
    Modal,
    Platform,
    Pressable,
    ScrollView,
    StyleSheet,
    Text,
    TextInput,
    TouchableOpacity,
    View,
} from 'react-native';
import {useTranslation} from 'react-i18next';
import {LinearGradient} from 'expo-linear-gradient';
import Icon from '../management/SafeIcon';
import {BRAND, useManagementTheme} from '../../helpers/managementTheme';
import {Card, EmptyState, PrimaryButton, StatusBadge} from '../management/ManagementUI';
import {IconChip} from '../premium/PremiumKit';

/** Minimum touch target of every driver action (pt). */
export const TOUCH_TARGET = 52;

/* -------------------------------------------------------------------------- */
/*  Sheet shell                                                                */
/* -------------------------------------------------------------------------- */

/**
 * Premium bottom-sheet shell (SOLID, never glass): grabber, header (tinted icon
 * chip + title + close), scrolling body and a sticky footer with an outlined
 * cancel pill + the orange primary pill (white text).
 *
 * @param {object} props
 * @param {boolean} props.visible Whether the sheet is shown.
 * @param {string} props.title Sheet title.
 * @param {string} [props.subtitle] Muted line under the title.
 * @param {string} [props.icon] MDI glyph of the header chip.
 * @param {boolean} [props.busy] Blocks dismissal while a request is in flight.
 * @param {Function} props.onClose Dismiss handler.
 * @param {string} [props.confirmLabel] Primary button label (omit to hide it).
 * @param {string} [props.confirmIcon] Primary button glyph.
 * @param {string} [props.cancelLabel] Outlined cancel button label (default "Mégse").
 * @param {Function} [props.onConfirm] Primary button handler.
 * @param {boolean} [props.confirmDisabled] Disable the primary button.
 * @param {boolean} [props.destructive] Danger-tone primary.
 * @param {React.ReactNode} props.children Sheet body.
 * @returns {JSX.Element} The sheet.
 */
export const DriverSheet = ({
    visible,
    title,
    subtitle,
    icon = 'truck-delivery-outline',
    busy = false,
    onClose,
    confirmLabel,
    confirmIcon,
    cancelLabel,
    onConfirm,
    confirmDisabled = false,
    destructive = false,
    children,
}) => {
    const {t} = useTranslation();
    const m = useManagementTheme();

    const dismiss = useCallback(() => {
        if (!busy) {
            onClose?.();
        }
    }, [busy, onClose]);

    const blocked = busy || confirmDisabled;

    return (
        <Modal visible={!!visible} transparent animationType="slide" onRequestClose={dismiss}>
            <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : undefined} style={styles.backdrop}>
                <Pressable style={styles.backdropFill} onPress={dismiss} />
                <View style={[styles.sheet, {backgroundColor: m.colors.surface, borderColor: m.colors.border}]}>
                    <View style={styles.grabber}><View style={[styles.grabberBar, {backgroundColor: m.colors.border}]} /></View>

                    <View style={styles.header}>
                        <IconChip icon={icon} tone="primary" size={46} style={styles.headerIcon} />
                        <View style={styles.headerText}>
                            <Text style={[styles.title, {color: m.colors.heading}]} numberOfLines={2}>{title}</Text>
                            {!!subtitle && <Text style={[styles.subtitle, {color: m.colors.subText}]} numberOfLines={2}>{subtitle}</Text>}
                        </View>
                        <TouchableOpacity
                            onPress={dismiss}
                            hitSlop={10}
                            disabled={busy}
                            accessibilityRole="button"
                            style={[styles.closeBtn, {backgroundColor: m.colors.surfaceAlt, opacity: busy ? 0.4 : 1}]}
                        >
                            <Icon name="close" size={22} color={m.colors.subText} />
                        </TouchableOpacity>
                    </View>

                    <ScrollView
                        showsVerticalScrollIndicator={false}
                        contentContainerStyle={styles.body}
                        keyboardShouldPersistTaps="handled"
                    >
                        {children}
                    </ScrollView>

                    <View style={[styles.footer, {borderTopColor: m.colors.border}]}>
                        <TouchableOpacity
                            activeOpacity={0.85}
                            disabled={busy}
                            onPress={dismiss}
                            accessibilityRole="button"
                            style={[styles.footerBtn, {borderWidth: 1, borderColor: m.colors.border, backgroundColor: m.colors.surface, opacity: busy ? 0.5 : 1}]}
                        >
                            <Text style={[styles.footerBtnText, {color: m.colors.subText}]}>
                                {cancelLabel || t('driver_action_cancel', {defaultValue: 'Mégse'})}
                            </Text>
                        </TouchableOpacity>
                        {!!confirmLabel && (
                            <TouchableOpacity
                                activeOpacity={0.85}
                                disabled={blocked}
                                onPress={onConfirm}
                                accessibilityRole="button"
                                accessibilityState={{disabled: blocked}}
                                style={[
                                    styles.footerBtn,
                                    styles.footerPrimary,
                                    !blocked && (destructive ? null : styles.primaryGlow),
                                    {
                                        backgroundColor: destructive ? m.colors.danger : m.colors.primary,
                                        opacity: blocked ? 0.55 : 1,
                                    },
                                ]}
                            >
                                {busy ? <ActivityIndicator color="#FFFFFF" /> : (
                                    <>
                                        {!!confirmIcon && <Icon name={confirmIcon} size={19} color="#FFFFFF" style={{marginRight: 7}} />}
                                        <Text style={[styles.footerBtnText, {color: '#FFFFFF'}]} numberOfLines={1}>{confirmLabel}</Text>
                                    </>
                                )}
                            </TouchableOpacity>
                        )}
                    </View>
                </View>
            </KeyboardAvoidingView>
        </Modal>
    );
};

/* -------------------------------------------------------------------------- */
/*  Buttons                                                                    */
/* -------------------------------------------------------------------------- */

/** Gradient overrides: the stock `warning` tone is too pale for white text. */
const TONE_FILL = {
    warning: ['#D97706', '#F59E0B'],
};

/**
 * Large pill action button (icon over label optional) for on-road use. The
 * primary tone is the orange brand gradient; every filled tone carries white text.
 *
 * @param {object} props
 * @param {string} props.label Button text.
 * @param {string} [props.icon] MDI glyph.
 * @param {Function} props.onPress Handler.
 * @param {string} [props.tone='primary'] Gradient tone.
 * @param {boolean} [props.outline] Outlined (transparent) variant.
 * @param {boolean} [props.disabled] Disabled state.
 * @param {boolean} [props.loading] Shows a spinner.
 * @param {boolean} [props.compact] Equal-width cell inside an action row.
 * @returns {JSX.Element} The button.
 */
export const BigAction = ({label, icon, onPress, tone = 'primary', outline = false, disabled = false, loading = false, compact = false}) => {
    const m = useManagementTheme();
    const fg = outline ? m.colors.heading : '#FFFFFF';
    const content = loading ? <ActivityIndicator color={outline ? m.colors.primary : fg} /> : (
        <>
            {!!icon && <Icon name={icon} size={compact ? 20 : 22} color={outline ? m.colors.primary : fg} style={compact ? null : {marginRight: 9}} />}
            <Text style={[compact ? styles.actionTextCompact : styles.actionText, {color: fg}]} numberOfLines={compact ? 2 : 1}>{label}</Text>
        </>
    );

    if (outline) {
        return (
            <TouchableOpacity
                activeOpacity={0.8}
                onPress={onPress}
                disabled={disabled || loading}
                accessibilityRole="button"
                accessibilityState={{disabled: disabled || loading}}
                style={[
                    styles.action,
                    compact && styles.actionCompact,
                    compact && styles.actionCompactWrap,
                    {borderWidth: 1, borderColor: m.colors.border, backgroundColor: m.colors.surface, opacity: disabled ? 0.5 : 1},
                ]}
            >
                {content}
            </TouchableOpacity>
        );
    }

    return (
        <TouchableOpacity
            activeOpacity={0.85}
            onPress={onPress}
            disabled={disabled || loading}
            accessibilityRole="button"
            accessibilityState={{disabled: disabled || loading}}
            style={[compact && styles.actionCompactWrap, {opacity: disabled ? 0.5 : 1}]}
        >
            <LinearGradient
                colors={TONE_FILL[tone] || m.toneGradient(tone)}
                start={{x: 0, y: 0}}
                end={{x: 1, y: 0}}
                style={[styles.action, compact && styles.actionCompact, tone === 'primary' ? styles.primaryGlow : m.shadow(1)]}
            >
                {content}
            </LinearGradient>
        </TouchableOpacity>
    );
};

/**
 * Horizontal row that spreads {@link BigAction} cells evenly.
 * @param {{children: React.ReactNode, style?: object}} props Props.
 * @returns {JSX.Element} The row.
 */
export const ActionRow = ({children, style}) => <View style={[styles.actionRow, style]}>{children}</View>;

/**
 * Quantity stepper with 52 pt round buttons (minus tinted, plus orange).
 *
 * @param {object} props
 * @param {number} props.value Current quantity.
 * @param {(next: number) => void} props.onChange Change handler.
 * @param {number} [props.min=0] Lower bound.
 * @param {number} [props.max] Upper bound (omit for none).
 * @returns {JSX.Element} The stepper.
 */
export const QtyStepper = ({value, onChange, min = 0, max}) => {
    const m = useManagementTheme();
    const canMinus = value > min;
    const canPlus = max === undefined || value < max;

    return (
        <View style={styles.stepper}>
            <TouchableOpacity
                disabled={!canMinus}
                onPress={() => onChange(Math.max(min, value - 1))}
                style={[styles.stepBtn, {backgroundColor: m.colors.surfaceAlt, borderColor: m.colors.border, opacity: canMinus ? 1 : 0.4}]}
                accessibilityRole="button"
                accessibilityLabel="-"
            >
                <Icon name="minus" size={22} color={m.colors.heading} />
            </TouchableOpacity>
            <Text style={[styles.stepValue, {color: m.colors.heading}]}>{value}</Text>
            <TouchableOpacity
                disabled={!canPlus}
                onPress={() => onChange(max === undefined ? value + 1 : Math.min(max, value + 1))}
                style={[styles.stepBtn, {backgroundColor: m.colors.primary, borderColor: m.colors.primary, opacity: canPlus ? 1 : 0.4}]}
                accessibilityRole="button"
                accessibilityLabel="+"
            >
                <Icon name="plus" size={22} color="#FFFFFF" />
            </TouchableOpacity>
        </View>
    );
};

/* -------------------------------------------------------------------------- */
/*  Chips + search                                                             */
/* -------------------------------------------------------------------------- */

/**
 * Horizontally scrolling single-select chip bar — pill chips, the selected one
 * is the bright orange capsule with white text.
 *
 * @param {object} props
 * @param {Array<{value: string, label: string, count?: number}>} props.options Options.
 * @param {string} props.value Selected value.
 * @param {(value: string) => void} props.onChange Change handler.
 * @returns {JSX.Element} The chip bar.
 */
export const ChipBar = ({options, value, onChange}) => {
    const m = useManagementTheme();

    return (
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.chipScroll} contentContainerStyle={styles.chipBar}>
            {options.map((option) => {
                const active = option.value === value;

                return (
                    <TouchableOpacity
                        key={option.value}
                        activeOpacity={0.8}
                        onPress={() => onChange(option.value)}
                        hitSlop={{top: 4, bottom: 4}}
                        accessibilityRole="button"
                        accessibilityState={{selected: active}}
                        style={[
                            styles.chip,
                            active
                                ? {backgroundColor: m.colors.primary, borderColor: m.colors.primary}
                                : {backgroundColor: m.colors.surface, borderColor: m.colors.border},
                        ]}
                    >
                        <Text style={[styles.chipText, {color: active ? '#FFFFFF' : m.colors.subText}]}>
                            {option.label}{option.count !== undefined ? `  ${option.count}` : ''}
                        </Text>
                    </TouchableOpacity>
                );
            })}
        </ScrollView>
    );
};

/**
 * Pill search field shared by the driver lists and pickers.
 *
 * @param {object} props
 * @param {string} props.value Text.
 * @param {(text: string) => void} props.onChangeText Change handler.
 * @param {string} props.placeholder Placeholder.
 * @param {boolean} [props.loading] Shows a spinner on the right.
 * @param {object} [props.style] Extra container style.
 * @returns {JSX.Element} The field.
 */
export const SearchField = ({value, onChangeText, placeholder, loading = false, style}) => {
    const m = useManagementTheme();

    return (
        <View style={[styles.search, {backgroundColor: m.colors.surface, borderColor: m.colors.border}, style]}>
            <Icon name="magnify" size={22} color={m.colors.muted} />
            <TextInput
                value={value}
                onChangeText={onChangeText}
                placeholder={placeholder}
                placeholderTextColor={m.colors.placeholder}
                style={[styles.searchInput, {color: m.colors.heading}]}
                autoCorrect={false}
            />
            {loading && <ActivityIndicator size="small" color={m.colors.primary} />}
        </View>
    );
};

/* -------------------------------------------------------------------------- */
/*  Skeletons & states                                                         */
/* -------------------------------------------------------------------------- */

/**
 * Pulsing placeholder block.
 * @param {{height?: number, width?: number|string, radius?: number, style?: object}} props Props.
 * @returns {JSX.Element} The block.
 */
export const SkeletonBlock = ({height = 72, width = '100%', radius = 22, style}) => {
    const m = useManagementTheme();
    const pulse = useRef(new Animated.Value(0.45)).current;

    useEffect(() => {
        const loop = Animated.loop(Animated.sequence([
            Animated.timing(pulse, {toValue: 1, duration: 700, useNativeDriver: true}),
            Animated.timing(pulse, {toValue: 0.45, duration: 700, useNativeDriver: true}),
        ]));

        loop.start();

        return () => loop.stop();
    }, [pulse]);

    return (
        <Animated.View
            style={[{height, width, borderRadius: radius, backgroundColor: m.colors.border, opacity: pulse, marginBottom: 12}, style]}
        />
    );
};

/**
 * A stack of skeleton rows used as the list loading state.
 * @param {{count?: number, height?: number}} props Props.
 * @returns {JSX.Element} The skeleton list.
 */
export const SkeletonList = ({count = 4, height = 84}) => (
    <View>
        {Array.from({length: count}).map((_, index) => <SkeletonBlock key={index} height={height} />)}
    </View>
);

/**
 * Full-card state shown when the store switched the driver module off.
 * @returns {JSX.Element} The empty state.
 */
export const ModuleDisabledState = () => {
    const {t} = useTranslation();

    return (
        <EmptyState
            icon="truck-remove-outline"
            tone="warning"
            title={t('driver_module_disabled_title', {defaultValue: 'A Sofőr app nincs bekapcsolva'})}
            text={t('driver_module_disabled_text', {defaultValue: 'Ennél az üzletnél a Sofőr / Futár modul ki van kapcsolva. Kérd meg az adminisztrátort, hogy kapcsolja be.'})}
        />
    );
};

/**
 * Load-failure card with a retry button.
 * @param {{message?: string, onRetry?: Function}} props Props.
 * @returns {JSX.Element} The card.
 */
export const LoadFailedState = ({message, onRetry}) => {
    const {t} = useTranslation();

    return (
        <EmptyState
            icon="cloud-off-outline"
            tone="danger"
            title={t('driver_load_failed', {defaultValue: 'Nem sikerült betölteni az adatokat'})}
            text={message || t('driver_load_failed_text', {defaultValue: 'Húzd le a frissítéshez, vagy próbáld újra.'})}
            action={onRetry ? <PrimaryButton label={t('driver_action_retry', {defaultValue: 'Újra'})} icon="refresh" onPress={onRetry} /> : null}
        />
    );
};

/* -------------------------------------------------------------------------- */
/*  Small display pieces                                                       */
/* -------------------------------------------------------------------------- */

/**
 * Numbered stop badge coloured by its visit status.
 * @param {{index: number|string, tone?: string, size?: number}} props Props.
 * @returns {JSX.Element} The badge.
 */
export const StopNumber = ({index, tone = 'neutral', size = 36}) => {
    const m = useManagementTheme();
    const bg = {
        success: m.colors.success,
        danger: m.colors.danger,
        warning: m.colors.warning,
        info: m.colors.info,
        neutral: m.colors.muted,
    }[tone] || m.colors.muted;

    return (
        <View style={[styles.stopNumber, {width: size, height: size, borderRadius: size / 2, backgroundColor: bg}]}>
            <Text style={styles.stopNumberText}>{index}</Text>
        </View>
    );
};

/**
 * Label / value line used on the detail screens.
 * @param {{label: string, value: *, last?: boolean, strong?: boolean}} props Props.
 * @returns {JSX.Element} The row.
 */
export const KeyValue = ({label, value, last = false, strong = false}) => {
    const m = useManagementTheme();

    return (
        <View style={[styles.kv, !last && {borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: m.colors.border}]}>
            <Text style={[styles.kvLabel, {color: m.colors.subText}]}>{label}</Text>
            <Text style={[styles.kvValue, {color: m.colors.heading}, strong && {fontWeight: '800', fontSize: 18}]} numberOfLines={2}>
                {value === undefined || value === null || value === '' ? '—' : String(value)}
            </Text>
        </View>
    );
};

/**
 * Capsule progress bar (0..1) with an orange fill.
 * @param {{progress: number, onDark?: boolean}} props `onDark` paints the track for the navy canvas.
 * @returns {JSX.Element} The bar.
 */
export const ProgressBar = ({progress, onDark = false}) => {
    const m = useManagementTheme();
    const clamped = Math.max(0, Math.min(1, Number(progress) || 0));

    return (
        <View style={[styles.progressTrack, {backgroundColor: onDark ? 'rgba(255,255,255,0.14)' : (m.isDark ? 'rgba(148,163,184,0.2)' : 'rgba(100,116,139,0.14)')}]}>
            <View style={[styles.progressFill, {width: `${clamped * 100}%`}]} />
        </View>
    );
};

/**
 * Card header line: soft tinted icon chip + title/subtitle + optional right slot.
 * @param {{icon: string, title: string, subtitle?: string, right?: React.ReactNode, tone?: string}} props Props.
 * @returns {JSX.Element} The line.
 */
export const CardHead = ({icon, title, subtitle, right, tone = 'primary'}) => {
    const m = useManagementTheme();

    return (
        <View style={styles.cardHead}>
            <IconChip icon={icon} tone={tone} size={46} />
            <View style={{flex: 1}}>
                <Text style={[styles.cardHeadTitle, {color: m.colors.heading}]} numberOfLines={2}>{title}</Text>
                {!!subtitle && <Text style={[styles.cardHeadSub, {color: m.colors.subText}]} numberOfLines={2}>{subtitle}</Text>}
            </View>
            {right}
        </View>
    );
};

export {Card, StatusBadge};

const styles = StyleSheet.create({
    backdrop: {flex: 1, justifyContent: 'flex-end', backgroundColor: 'rgba(15,18,48,0.58)'},
    backdropFill: {...StyleSheet.absoluteFillObject},
    sheet: {borderTopLeftRadius: 32, borderTopRightRadius: 32, borderWidth: StyleSheet.hairlineWidth, maxHeight: '90%', paddingBottom: Platform.OS === 'ios' ? 28 : 12},
    grabber: {alignItems: 'center', paddingTop: 10},
    grabberBar: {width: 44, height: 5, borderRadius: 3},
    header: {flexDirection: 'row', alignItems: 'center', paddingHorizontal: 20, paddingTop: 12, paddingBottom: 6},
    headerIcon: {marginRight: 12},
    headerText: {flex: 1},
    title: {fontSize: 20, fontWeight: '800', letterSpacing: -0.3},
    subtitle: {fontSize: 13, fontWeight: '600', marginTop: 2},
    closeBtn: {width: 44, height: 44, borderRadius: 22, alignItems: 'center', justifyContent: 'center'},
    body: {paddingHorizontal: 20, paddingTop: 10, paddingBottom: 8},
    footer: {flexDirection: 'row', gap: 12, paddingHorizontal: 20, paddingTop: 12, borderTopWidth: StyleSheet.hairlineWidth},
    footerBtn: {flex: 1, minHeight: TOUCH_TARGET + 4, borderRadius: 28, alignItems: 'center', justifyContent: 'center', flexDirection: 'row', paddingHorizontal: 12},
    footerPrimary: {flex: 1.4},
    footerBtnText: {fontSize: 16, fontWeight: '800'},
    primaryGlow: {shadowColor: BRAND.dark, shadowOffset: {width: 0, height: 6}, shadowOpacity: 0.26, shadowRadius: 12, elevation: 4},

    action: {minHeight: TOUCH_TARGET + 4, borderRadius: 28, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', paddingHorizontal: 20},
    actionCompact: {flexDirection: 'column', minHeight: 68, paddingHorizontal: 6, paddingVertical: 8, borderRadius: 22},
    actionCompactWrap: {flex: 1},
    actionText: {fontSize: 16, fontWeight: '800'},
    actionTextCompact: {fontSize: 12, fontWeight: '800', marginTop: 4, textAlign: 'center'},
    actionRow: {flexDirection: 'row', gap: 10, alignItems: 'stretch'},

    stepper: {flexDirection: 'row', alignItems: 'center'},
    stepBtn: {width: TOUCH_TARGET, height: TOUCH_TARGET, borderRadius: TOUCH_TARGET / 2, borderWidth: 1, alignItems: 'center', justifyContent: 'center'},
    stepValue: {minWidth: 42, textAlign: 'center', fontSize: 20, fontWeight: '800'},

    chipScroll: {flexGrow: 0},
    chipBar: {paddingVertical: 4, gap: 8, paddingRight: 8},
    chip: {minHeight: 48, borderRadius: 24, borderWidth: 1, paddingHorizontal: 20, alignItems: 'center', justifyContent: 'center'},
    chipText: {fontSize: 14.5, fontWeight: '800'},

    search: {flexDirection: 'row', alignItems: 'center', gap: 10, borderWidth: 1, borderRadius: 999, paddingHorizontal: 18, minHeight: TOUCH_TARGET + 2},
    searchInput: {flex: 1, fontSize: 16, fontWeight: '600', paddingVertical: 10},

    stopNumber: {alignItems: 'center', justifyContent: 'center'},
    stopNumberText: {color: '#FFFFFF', fontWeight: '800', fontSize: 15},

    kv: {flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 12, gap: 12},
    kvLabel: {fontSize: 14, fontWeight: '600', flexShrink: 0},
    kvValue: {fontSize: 15, fontWeight: '800', flex: 1, textAlign: 'right'},

    progressTrack: {height: 8, borderRadius: 4, overflow: 'hidden'},
    progressFill: {height: 8, borderRadius: 4, backgroundColor: BRAND.base},

    cardHead: {flexDirection: 'row', alignItems: 'center', gap: 12},
    cardHeadTitle: {fontSize: 17, fontWeight: '800', letterSpacing: -0.2},
    cardHeadSub: {fontSize: 13, fontWeight: '600', marginTop: 2},
});
